import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useGetHoFRecords,
  invalidateHoFRecords,
  useHoFQueueCounts,
} from 'actions/hall-of-fame';

import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_QUEUE_KEY,
  QUEUES,
  SEARCH_FETCH_LIMIT,
} from '../constants';
import { asPage, matchesSearch, searchTerms } from '../queue-fields';

const ALL_RECORDS = '';

// The whole table, unfiltered by status: a search crosses every queue, because the moderator
// looking up a student does not know which one the row is sitting in. Module-level so its
// identity is stable — it is a dependency of the effect that fires the request.
const SEARCH_QUERY = { limit: SEARCH_FETCH_LIMIT, includeCount: true };

// Which queue is open, which page of it, and the counts on the tiles. One hook because
// changing the queue changes the page, and any of them changes what has to be refetched.
const useHoFQueue = ({ examNames = {} } = {}) => {
  const [queueKey, setQueueKey] = useState(DEFAULT_QUEUE_KEY);
  const [page, setPage] = useState(1); // 1-based, matching modules/list/pagination
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');

  // One instance per kind of request, and the split is load bearing: the store notifies its
  // listeners inside a `setTimeout(0)`, so `data` lags the state that decides how to read it.
  // Shared, the render that ends a search still held the whole table and committed every row.
  const paged = useGetHoFRecords();
  const searched = useGetHoFRecords();
  const { counts, fetchCounts } = useHoFQueueCounts();

  const terms = searchTerms(search);
  const isSearching = terms.length > 0;
  const activeQueue = useMemo(() => QUEUES.find(q => q.key === queueKey), [queueKey]);

  // `includeCount` goes on every page, not just the first: publishing, removing or a fresh
  // ingest changes the total while you are paging, and a stale total renders the wrong
  // number of page links.
  //
  // `null` while searching, deliberately: the memo recomputes on a page turn but two nulls
  // compare equal, so `fetchRecords` keeps its identity and the effect does not re-run.
  // `getAll` has no cache, and that round trip is the whole table plus a name lookup each.
  const pageQuery = useMemo(
    () =>
      isSearching
        ? null
        : {
            ...(activeQueue ? activeQueue.query : {}),
            limit: pageSize,
            offset: (page - 1) * pageSize,
            includeCount: true,
          },
    [isSearching, activeQueue, pageSize, page],
  );

  const getPage = paged.getAll;
  const getWholeTable = searched.getAll;

  const fetchRecords = useCallback(() => {
    if (pageQuery) getPage(pageQuery);
    else getWholeTable(SEARCH_QUERY);
  }, [getPage, getWholeTable, pageQuery]);

  const active = isSearching ? searched : paged;
  const { items: fetched, count: fetchedTotal } = asPage(active.data);

  // Why the filter is here and not in the query: see `SEARCH_FETCH_LIMIT`. Not memoized —
  // ~450 rows of three string comparisons, and the rows themselves are memoized downstream.
  const matched = isSearching
    ? fetched.filter(row => matchesSearch(row, terms, examNames))
    : fetched;

  // A searched page is cut here, because the service was never asked to cut it — and its
  // count is the number of matches, not the number of rows fetched to find them.
  const total = isSearching ? matched.length : fetchedTotal;
  const records = isSearching
    ? matched.slice((page - 1) * pageSize, page * pageSize)
    : matched;

  // Fewer rows than the service says exist: the fetch hit `SEARCH_FETCH_LIMIT`, and the rows
  // past the cut come back unnamed, so a name search cannot see them.
  const isTruncated = isSearching && fetchedTotal > fetched.length;

  // Not the hook's own `error`, which is always undefined: `changeMeta` records a failure
  // under `__ERROR__`, never under the `error` key its type declares. Unread, a failed search
  // renders as "Nothing matches Priya" — a claim about the student, not about the request.
  const failedResource = active.isProcessingFailed ? active.resource : null;
  const error = failedResource
    ? (failedResource.__ERROR__ || {}).message || 'Could not load records.'
    : null;

  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const firstOnPage = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastOnPage = Math.min(page * pageSize, total);

  // Anything that changes a record moves it between tiles, so the counts go stale at the
  // same moment the list does.
  const refresh = useCallback(() => {
    invalidateHoFRecords();
    fetchRecords();
    fetchCounts(QUEUES);
  }, [fetchRecords, fetchCounts]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    fetchCounts(QUEUES);
  }, [fetchCounts]);

  // Removing the last row of the last page would otherwise strand you on an empty page,
  // with no way back except the pager you can no longer see.
  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  // A tile filters by status and a search crosses every status, so both at once would mean
  // showing rows the highlighted tile excludes. Picking a tile is the moderator saying they
  // are done looking something up, so it clears the search rather than fighting it.
  const changeQueue = useCallback(value => {
    setQueueKey(value);
    setSearch('');
    setPage(1);
  }, []);

  const changeSearch = useCallback(value => {
    setSearch(value);
    setPage(1);
  }, []);

  const changePageSize = useCallback(value => {
    setPageSize(Number(value));
    setPage(1);
  }, []);

  const showAllRecords = useCallback(() => changeQueue(ALL_RECORDS), [changeQueue]);

  const queueHeading = activeQueue ? activeQueue.label : 'All records';

  return {
    queueKey,
    // Which tile is lit. Nothing is, while a search is open: the tile's filter is not being
    // applied, and a highlighted tile that is not filtering is a lie about what is on screen.
    activeKey: isSearching ? null : queueKey,
    changeQueue,
    showAllRecords,
    search,
    changeSearch,
    isSearching,
    // "View all records" clears a tile, and while searching there is no tile to clear.
    isFiltered: !isSearching && queueKey !== ALL_RECORDS,
    heading: isSearching ? 'Search results' : queueHeading,
    records,
    total,
    counts,
    error,
    isTruncated,
    // "No answer of this kind yet" — not "no rows" (the first search would say "nothing
    // matches" before asking), not `isProcessing` (a search matching nothing must say so,
    // not spin), not `isProcessed` (a PENDING write clears it while keeping the data).
    isFirstLoad: active.data === undefined && !active.isProcessingFailed,
    isRefreshing: active.isProcessing && !!records.length,
    isBusy: active.isProcessing,
    page,
    setPage,
    pageSize,
    changePageSize,
    lastPage,
    firstOnPage,
    lastOnPage,
    refresh,
  };
};

export default useHoFQueue;
